import React, { useState, useEffect } from 'react';
import './DraftTutorial.css';

/* ─── Shared K logo icon — used in every step header ────────────────────── */

const K_LOGO_ICON = (
  <img src="/kickopp-k-logo.svg" alt="Kickopp"
       style={{ width: '56px', height: '56px', objectFit: 'contain' }} />
);

/* ─── Step illustrations ─────────────────────────────────────────────────── */

function PlayerCardDecksIllustration() {
  // Matches actual game board deck panel — full names + total card counts per position group
  const decks = [
    { label: 'FORWARD',          count: 16, col: '#3b82f6' },
    { label: 'MIDFIELDER\nWIDE', count: 12, col: '#a855f7' },
    { label: 'MIDFIELDER\nCENTER',count: 20, col: '#10b981' },
    { label: 'DEFENDER\nWIDE',   count: 12, col: '#f97316' },
    { label: 'DEFENDER\nCENTER', count: 20, col: '#ef4444' },
    { label: 'KEEPER',           count:  8, col: '#fbbf24' },
  ];
  return (
    <div className="dt-visual dt-visual--player-decks">
      <div className="dt-visual__board-panel">
        {decks.map((d, i) => (
          <div key={i} className="dt-visual__pdeck" style={{ '--dc': d.col }}>
            <div className="dt-visual__pdeck-bar" />
            <div className="dt-visual__pdeck-name">
              {d.label.split('\n').map((line, j) => (
                <span key={j}>{line}</span>
              ))}
            </div>
            <div className="dt-visual__pdeck-count">{d.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TacticsIllustration() {
  // Colors + positions match the actual in-game 4-3-3 tactic card.
  // Fractions shown as all-complete so the player can see what full checks look like.
  // 0/0 slot is always satisfied (no requirement) — shown with its green ring.
  const W = '#ffffff';  // white for the + separator
  const slots = [
    // parts: each segment has its own colour; '+' is always white
    { fraction: '1/1', col: '#eab308', parts: [{ t: 'GK', c: '#eab308' }, { t: ' + ', c: W }, { t: 'CM', c: '#16a34a' }] },
    { fraction: '2/2', col: '#dc2626', parts: [{ t: 'DCB', c: '#dc2626' }] },
    { fraction: '2/2', col: '#ea580c', parts: [{ t: 'FB', c: '#ea580c' }, { t: ' + ', c: W }, { t: 'FB', c: '#ea580c' }] },
    { fraction: '3/3', col: '#16a34a', parts: [{ t: 'DM', c: '#16a34a' }] },
    { fraction: '0/0', col: '#a855f7', parts: [{ t: 'DF', c: '#a855f7' }], free: true },
    { fraction: '3/3', col: '#3b82f6', parts: [{ t: 'WF', c: '#3b82f6' }] },
  ];
  return (
    <div className="dt-visual dt-visual--tactic-card">

      {/* Left: formation name */}
      <div className="dt-visual__tc-formation">
        <div className="dt-visual__tc-name">4-3-3</div>
      </div>

      {/* Centre: position slot rows */}
      <div className="dt-visual__tc-slots">

        {/* Fraction row — neutral fill, coloured border + text */}
        <div className="dt-visual__tc-fractions">
          {slots.map((s, i) => (
            <div key={i} className="dt-visual__tc-slot" style={{ '--sc': s.col }}>
              {s.fraction}
            </div>
          ))}
        </div>

        {/* Position label row — in bordered boxes, each part coloured individually */}
        <div className="dt-visual__tc-labels">
          {slots.map((s, i) => (
            <div key={i} className="dt-visual__tc-label" style={{ '--sc': s.col }}>
              {s.parts.map((p, j) => (
                <span key={j} style={{ color: p.c }}>{p.t}</span>
              ))}
            </div>
          ))}
        </div>

        {/* Checkmark row — green ticks show each position can be fulfilled */}
        <div className="dt-visual__tc-checks">
          {slots.map((_, i) => (
            <div key={i} className="dt-visual__tc-check">✓</div>
          ))}
        </div>

      </div>

      {/* Right: boost rewards — both earned */}
      <div className="dt-visual__tc-boosts">
        <div className="dt-visual__tc-boost dt-visual__tc-boost--atk">+ 4</div>
        <div className="dt-visual__tc-boost dt-visual__tc-boost--def">+ 2</div>
      </div>

    </div>
  );
}

function DraftWindowIllustration() {
  // All cards are from the Mid. Center deck → all use MC green (#10b981)
  // Only the outcome badge changes color to signal what happens to each card
  const MC = '#10b981';
  const cards = [
    {
      atk: 12, def: 10, name: 'S. Pereyra', pos: 'AM',
      outcome: 'lineup',
      badgeBg: '#10b981', badgeText: '✓ 1st Pick → Lineup',
      raised: true, dim: false,
    },
    {
      atk:  9, def: 14, name: 'R. Bosman',  pos: 'DM',
      outcome: 'bench',
      badgeBg: '#f59e0b', badgeText: '2nd Pick → Bench',
      raised: false, dim: false,
    },
    {
      atk:  9, def: 13, name: 'A. Boban',   pos: 'CM',
      outcome: 'discard',
      badgeBg: '#ef4444', badgeText: '✕ Not chosen',
      raised: false, dim: true,
    },
  ];

  return (
    <div className="dt-visual dt-visual--draft">
      <div className="dt-visual__draft-label">Mid. Center deck — 3 cards drawn</div>
      <div className="dt-visual__draft-cards">
        {cards.map((c, i) => (
          <div
            key={i}
            className="dt-visual__draft-card"
            style={{
              '--cc': MC,
              border: `1.5px solid ${MC}${c.dim ? '44' : 'bb'}`,
              boxShadow: c.raised ? `0 0 20px -4px ${MC}, 0 8px 24px rgba(0,0,0,0.4)` : 'none',
              transform: c.raised ? 'translateY(-8px) scale(1.05)' : 'none',
              opacity: c.dim ? 0.4 : 1,
            }}
          >
            {/* Outcome badge above card */}
            <div style={{
              position: 'absolute', top: '-24px', left: '50%', transform: 'translateX(-50%)',
              background: c.badgeBg, color: 'white',
              fontSize: '10px', fontWeight: '800',
              padding: '3px 8px', borderRadius: '8px',
              whiteSpace: 'nowrap', letterSpacing: '0.2px',
            }}>{c.badgeText}</div>

            <div className="dt-visual__draft-stats">
              <span className="dt-visual__draft-atk">{c.atk}</span>
              <span className="dt-visual__draft-def">{c.def}</span>
            </div>

            {/* Clean face placeholder — no photo, no icon */}
            <div style={{
              height: '64px', borderRadius: '7px',
              background: `linear-gradient(135deg, ${MC}18, ${MC}08)`,
              border: `1px solid ${MC}30`,
            }} />

            <div className="dt-visual__draft-pos" style={{ background: MC }}>{c.pos}</div>
            <div className="dt-visual__draft-name">{c.name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Step definitions — all centered ───────────────────────────────────── */

const STEPS = [
  {
    id: 'welcome',
    icon: K_LOGO_ICON,
    accent: '#3b82f6',
    badge: 'DRAFT GUIDE',
    title: 'Welcome to Your First Draft in Kickopp',
    body: <>
      Let's walk you through a few things every beginner manager needs to know.
      <br />
      Takes less than a minute — you can always reopen this guide{' '}
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '30px', height: '30px', borderRadius: '7px',
        background: 'rgba(0,0,0,0.4)', border: '2px solid rgba(234,179,8,0.55)',
        color: '#eab308', fontSize: '16px', fontWeight: 900,
        verticalAlign: 'middle', margin: '0 3px', flexShrink: 0,
      }}>?</span>
      {' '}from the header.
    </>,
    Visual: null,
    hint: null,
  },
  {
    id: 'player-decks',
    icon: K_LOGO_ICON,
    accent: '#eab308',
    badge: '1/4 · PLAYER CARDS DECK',
    title: 'Your 6 Player Card Decks',
    body: <>
      Your Player Cards are split across 6 position decks on the board — one for each position group.
      <br />
      During the Draft you tap a deck to draw 3 cards.
      <br />
      You pick 1 Player to Play, 1 to Bench and 1 will be Discarded.
    </>,
    Visual: PlayerCardDecksIllustration,
    // Dynamic hint: function receives isVertical from the component
    hint: (isVertical) => isVertical
      ? '📍 Find the Player Card Decks panel on the right side of the board'
      : '📍 Find the Player Card Decks panel on the upper area of the board',
  },
  {
    id: 'tactics',
    icon: K_LOGO_ICON,
    accent: '#eab308',
    badge: '2/4 · YOUR TACTICS',
    title: 'Your 3 Possible Tactics',
    body: <>
      During your Draft, select Players with the 3 available Tactics in mind. Each Tactic tracks how many of its position checks you meet.
      <br />
      The numbers on the right show the maximum <span style={{ color: '#3b82f6', fontWeight: 900 }}>Attacking</span> and <span style={{ color: '#ef4444', fontWeight: 900 }}>Defensive</span> Boosts you can earn — the more position checks you match, the higher the boost you activate in the Warm Up.
    </>,
    Visual: TacticsIllustration,
    hint: '📍 Your available Tactic cards (chosen randomly each match) are in the bottom-left corner of the board',
  },
  {
    id: 'draft',
    icon: K_LOGO_ICON,
    accent: '#f97316',
    badge: '3/4 · DRAFT WINDOW',
    title: 'Draft Your Players',
    body: <>
      After you click on any available Position Deck, the Draft window will open.
      {' '}You will see <strong>3 Player cards</strong> from that deck — pick the <strong style={{ color: '#10b981' }}>1st</strong> to add to your lineup and the <strong style={{ color: '#f59e0b' }}>2nd</strong> for bench (placed back in the deck).
      {' '}After every position from your tactics is filled, the <strong style={{ color: '#f97316' }}>Warm‑Up begins!</strong>
    </>,
    Visual: DraftWindowIllustration,
    hint: '📍 Click a Position Deck below to open the Draft window',
  },
  {
    id: 'instruction-bar',
    icon: K_LOGO_ICON,
    accent: '#64748b',
    badge: '4/4 · YOUR GUIDE BAR',
    title: 'Always Know Your Next Step',
    body: <>
      At any point during the Draft, the{' '}
      <strong style={{ color: '#94a3b8' }}>instruction bar</strong> just below the stage label tells you
      exactly what action to take next — for example{' '}
      <em>"Select a Position Deck to Draft a Player"</em>.
      <br /><br />
      Tap the <strong style={{ color: '#ef4444' }}>✕</strong> to dismiss it once you're comfortable,
      or leave it visible as your guide throughout the match.
    </>,
    Visual: null,
    hint: '📍 Look just below the DRAFT label — the instruction bar is always there',
  },
];

/* ─── Main component ─────────────────────────────────────────────────────── */

export default function DraftTutorial({ onComplete, onSkip, isVertical = false }) {
  const [step, setStep]       = useState(0);
  const [animDir, setAnimDir] = useState('in');

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast  = step === STEPS.length - 1;

  const navigate = (delta) => {
    setAnimDir(delta > 0 ? 'out-left' : 'out-right');
    setTimeout(() => {
      setStep(s => s + delta);
      setAnimDir('in');
    }, 200);
  };

  const handleNext = () => isLast ? onComplete() : navigate(1);
  const handleBack = () => navigate(-1);

  /* Keyboard shortcuts */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') handleNext();
      if (e.key === 'ArrowLeft' && !isFirst) handleBack();
      if (e.key === 'Escape') onSkip();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const Visual = current.Visual;

  return (
    <div className="dt">
      {/* Dark overlay — click does NOT close, just dims the game */}
      <div className="dt__overlay" />

      {/* Step card — always centered */}
      <div
        className={`dt__card dt__card--${animDir}`}
        style={{ '--accent': current.accent }}
        onClick={e => e.stopPropagation()}
      >
        {/* X close button inside the card */}
        <button className="dt__close" onClick={onSkip} title="Close (Esc)">✕</button>

        {/* Accent top bar */}
        <div className="dt__accent-bar" />

        {/* Header row */}
        <div className="dt__header">
          <div className="dt__icon-wrap">
            <span className="dt__icon">{current.icon}</span>
          </div>
          <div className="dt__header-text">
            <div className="dt__badge">{current.badge}</div>
            <h2 className="dt__title">{current.title}</h2>
          </div>
        </div>

        {/* Body */}
        <p className="dt__body">{current.body}</p>

        {/* Location hint — supports plain string or function(isVertical) */}
        {current.hint && (
          <div className="dt__location-hint">
            {typeof current.hint === 'function' ? current.hint(isVertical) : current.hint}
          </div>
        )}

        {/* Visual illustration */}
        {Visual && (
          <div className="dt__visual-wrap">
            <Visual />
          </div>
        )}

        {/* Footer — dots + navigation */}
        <div className="dt__footer">
          <div className="dt__dots">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                className={`dt__dot ${i === step ? 'dt__dot--active' : ''} ${i < step ? 'dt__dot--done' : ''}`}
                style={i === step ? { background: current.accent, boxShadow: `0 0 8px ${current.accent}88` } : {}}
                onClick={() => { if (i !== step) { setAnimDir(i > step ? 'out-left' : 'out-right'); setTimeout(() => { setStep(i); setAnimDir('in'); }, 200); } }}
                aria-label={`Step ${i + 1}`}
              />
            ))}
          </div>

          <div className="dt__nav">
            {!isFirst && (
              <button className="dt__btn dt__btn--back" onClick={handleBack}>← Back</button>
            )}
            <button
              className="dt__btn dt__btn--next"
              style={{ background: current.accent, boxShadow: `0 6px 20px ${current.accent}55` }}
              onClick={handleNext}
            >
              {isFirst ? "Let's Go →" : isLast ? '🚀 Let\'s Play!' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
